/**
 * `afterRouteChange(prev, next)` (§5.1): passos 1–4 e 6–9. O passo 5 (zerar zoom e pan) é do próprio `features/map`,
 * que observa `viewKey` (DE-B07). Título da aba por vista (§5.3.4, M19) e alvos de foco ao navegar (§5.9).
 * O foco é injetável (`configureRouterEffects`): o App liga `focusIfKeyboard` de `a11y/focus.ts` quando ele existir.
 */
import type { Geography } from '../contracts/static';
import { t } from '../copy/strings';
import type { PlaceRoute, RouteOffice, RouteState } from '../model/types';
import { ctx, isSimulated, layout, route, ui } from '../state/store';
import { viewKey } from './selectors';
import type { FocusTarget, RouteChangeCause, RouteContext } from './types';

// ── Injeção ──────────────────────────────────────────────────────────────────────────────────────────────────
export interface RouterEffectsOptions {
  /** Move o foco (padrão: `el.focus({ preventScroll: true })`). O App injeta `focusIfKeyboard` (§15.2.1). */
  focus: (el: HTMLElement) => void;
  /** Agenda o foco para depois do render da vista nova (padrão: requestAnimationFrame). */
  schedule: (fn: () => void) => void;
  /** Rolagem do `flow` ao topo (§4.6; padrão: `scrollTo(0, 0)`). */
  scrollToTop: () => void;
  /** Acha o elemento do alvo (padrão: `resolveFocusTarget` no `document`). */
  resolve: (target: FocusTarget) => HTMLElement | null;
  /** Título da aba a partir do título da rota (padrão: o próprio). A página do replay põe a noite na frente. */
  title?: (base: string) => string;
}

const DEFAULTS: RouterEffectsOptions = {
  focus: (el) => el.focus({ preventScroll: true }),
  schedule: (fn) => {
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => fn());
    else setTimeout(fn, 0);
  },
  scrollToTop: () => {
    if (typeof scrollTo === 'function') scrollTo(0, 0);
  },
  resolve: (target) => resolveFocusTarget(target),
};

let options: RouterEffectsOptions = { ...DEFAULTS };

/** Troca as funções injetáveis; `null` volta aos padrões. */
export function configureRouterEffects(o: Partial<RouterEffectsOptions> | null): void {
  options = o === null ? { ...DEFAULTS } : { ...options, ...o };
}

// ── Estado do módulo ─────────────────────────────────────────────────────────────────────────────────────────
let pendingThen: (() => void) | null = null;
let routePending = false;

/** Guarda o `then` da última chamada a `navigate` (§5.3.1); o passo 9 o executa e descarta. */
export function setPendingThen(fn: (() => void) | null): void {
  pendingThen = fn;
}

/** Validação adiada da rota atual (§5.2.9): o título usa `title.generic` enquanto for true. */
export function setRoutePending(pending: boolean): void {
  routePending = pending;
}
export function isRoutePending(): boolean {
  return routePending;
}

// ── Título da aba (§5.3.4) ───────────────────────────────────────────────────────────────────────────────────
function product(): string {
  return import.meta.env.VITE_PRODUCT_NAME ?? '';
}

function officeLabel(office: RouteOffice, withPlace: boolean): string {
  switch (office) {
    case 'president':
      return t('title.office.president');
    case 'governor':
      return withPlace ? t('title.office.governor') : t('title.office.governors');
    case 'senate':
      return t('title.office.senate');
    case 'chamber':
      return t('title.office.chamber');
  }
}

/** Rótulo do lugar nas formas da tabela de §5.3.4; null quando o nome ainda não é conhecido (sem Geography). */
export function placeLabel(p: PlaceRoute, geo: Geography | null): string | null {
  switch (p.kind) {
    case 'br':
      return t('title.place.br');
    case 'abroad':
      return t('title.place.abroad');
    case 'uf':
      return geo?.places.find((x) => x.code === p.uf)?.name ?? null;
    case 'municipality': {
      const m = geo?.municipalities.find((x) => x.ibge === p.ibge);
      if (!m) return null;
      const vars = { MUN: m.name, SIGLA_UF: p.uf };
      return p.zone != null ? t('title.place.zone', { ...vars, ZONA: p.zone }) : t('title.place.municipality', vars);
    }
    case 'country': {
      const c = geo?.countries.find((x) => x.iso2.toUpperCase() === p.iso2);
      return c ? t('title.place.country', { PAIS: c.name }) : null;
    }
    case 'abroad_city': {
      const city = geo?.abroad_cities.find((x) => x.tse === p.tse);
      const c = geo?.countries.find((x) => x.iso2.toUpperCase() === p.iso2);
      return city && c ? t('title.place.city', { CIDADE: city.name, PAIS: c.name }) : null;
    }
  }
}

/**
 * `document.title` da rota (§5.3.4, M19): "{lugar} · {cargo} · Mapa Voto"; Brasil de Governadores, Senado e
 * Deputados só com o cargo; validação pendente (ou nome ainda desconhecido) → `title.generic`; simulação → prefixo.
 * O replay e o perfil não mudam o título.
 */
export function titleFor(
  r: Pick<RouteState, 'office' | 'place'>,
  c: Pick<RouteContext, 'geo'>,
  simulated: boolean,
  pending: boolean,
): string {
  const PRODUCT = product();
  let base: string;
  if (pending) {
    base = t('title.generic', { PRODUCT });
  } else if (r.office !== 'president' && r.place.kind === 'br') {
    base = t('title.officeOnly', { CARGO: officeLabel(r.office, false), PRODUCT });
  } else {
    const lugar = placeLabel(r.place, c.geo);
    base =
      lugar === null
        ? t('title.generic', { PRODUCT })
        : t('title.view', { LUGAR: lugar, CARGO: officeLabel(r.office, true), PRODUCT });
  }
  return simulated ? `${t('title.simPrefix')}${base}` : base;
}

/** O título da página inicial (fase `home`, ORD-F-091): o do `<title>` do `index.html`, com o prefixo da simulação. */
export function homeTitle(simulated: boolean, after = false): string {
  // 26/10: a abertura pós-apuração (fase `after`) tem o título dela, o mesmo do <title> do index.html
  const base = t(after ? 'title.after' : 'title.home', { PRODUCT: product() });
  return simulated ? `${t('title.simPrefix')}${base}` : base;
}

/** Grava o título da rota atual se ele mudou (passo 6). */
export function applyTitle(): void {
  if (typeof document === 'undefined') return;
  const base = titleFor(route.value, ctx.value, isSimulated.value, routePending);
  const next = options.title ? options.title(base) : base;
  if (document.title !== next) document.title = next;
}

// ── Foco (§5.9) ──────────────────────────────────────────────────────────────────────────────────────────────
const ABROAD_KINDS: ReadonlySet<PlaceRoute['kind']> = new Set(['abroad', 'country', 'abroad_city']);

/**
 * Alvo do foco depois de uma mudança de rota (§5.9, §15.2.2), ou null (o foco não muda). Pura.
 * - Revalidação pelos estáticos nunca move o foco (§15.2.2 regra 1).
 * - Troca de cargo (aba ou `1`–`4`) não move o foco.
 * - Voltar/Avançar: título principal da nova vista (§15.2.2 regra 12).
 * - Abrir ou subir de nível, ‹ › entre UFs, zona → município: `h2` do cartão novo.
 * - Fechar UF ou município para o Brasil: rótulo da UF; fechar o Exterior: `ExteriorChip` (ou o `ExteriorButton`).
 */
export function focusTargetFor(prev: RouteState, next: RouteState, cause: RouteChangeCause): FocusTarget | null {
  if (cause === 'statics') return null;
  if (cause === 'same') return next.place.kind === 'br' ? null : { kind: 'card' };
  if (prev.office !== next.office) return null;
  const p = prev.place;
  const n = next.place;
  if (viewKey(prev) === viewKey(next)) {
    const zoneChanged = p.kind === 'municipality' && n.kind === 'municipality' && p.zone !== n.zone;
    return zoneChanged ? { kind: 'card' } : null;
  }
  if (cause === 'history') return { kind: 'main' };
  if (n.kind !== 'br') return { kind: 'card' };
  if (p.kind === 'uf' || p.kind === 'municipality') return { kind: 'uf', uf: p.uf };
  if (ABROAD_KINDS.has(p.kind)) return { kind: 'exterior' };
  return null;
}

function isShown(el: Element | null | undefined): el is HTMLElement {
  if (!el || !(el instanceof HTMLElement) || !el.isConnected) return false;
  if (el.closest('[hidden],[inert]')) return false;
  if (typeof getComputedStyle === 'function') {
    const cs = getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden') return false;
  }
  return true;
}

/**
 * Elemento do alvo no documento. Nenhum valor da URL entra em seletor CSS (§5.2.10): a UF é comparada pelo `dataset`.
 * Alvo ausente ou oculto (ex.: rótulo podado pela anticolisão) → `h1` da página (a `Brand`, `tabindex="-1"`).
 */
export function resolveFocusTarget(target: FocusTarget, doc: Document = document): HTMLElement | null {
  let el: Element | null | undefined = null;
  switch (target.kind) {
    case 'card':
      el = doc.getElementById('place-title');
      break;
    case 'main':
      el = doc.getElementById('place-title') ?? doc.getElementById('hero-headline');
      break;
    case 'uf': {
      const all = [...doc.querySelectorAll<HTMLElement>('[data-uf]')].filter((x) => x.dataset.uf === target.uf);
      el = all.find((x) => x.classList.contains('uf-marker') && isShown(x)) ?? all.find(isShown);
      break;
    }
    case 'exterior': {
      const active = doc.activeElement;
      if (active instanceof HTMLElement && active.closest('[data-testid="exterior-button"]')) return active;
      el = doc.querySelector('[data-testid="exterior-chip"]') ?? doc.querySelector('.exterior-chip');
      break;
    }
  }
  if (isShown(el)) return el;
  const h1 = doc.querySelector('h1');
  return isShown(h1) ? h1 : null;
}

/**
 * Quadros de nova tentativa enquanto a marca da UF ou o chip do Exterior ainda não estão visíveis: ~1 s a 60 quadros
 * por segundo (sob carga, cada quadro demora mais). Com 8 quadros, a suíte inteira do Chromium ainda via o foco cair no
 * h1 de vez em quando (AC-15.2-05, 17/10).
 */
export const FOCUS_RETRY_FRAMES = 60;

/**
 * Passo 8 de §5.1. A marca da UF (ou o chip do Exterior) pode ainda estar escondida no 1º quadro: o mapa refaz as marcas
 * depois da troca de rota e, sob carga, um quadro não basta (e2e AC-15.2-05 no WebKit com 2 workers: o foco caía no h1).
 * Enquanto a resolução der o h1 de reserva, tenta de novo por até FOCUS_RETRY_FRAMES quadros; depois fica o h1.
 */
function scheduleFocus(target: FocusTarget, left = FOCUS_RETRY_FRAMES): void {
  options.schedule(() => {
    if (!ui.value.keyboard) return;
    const el = options.resolve(target);
    const fallback = (target.kind === 'uf' || target.kind === 'exterior') && el?.tagName === 'H1';
    if (fallback && left > 0) {
      scheduleFocus(target, left - 1);
      return;
    }
    if (el) options.focus(el);
  });
}

// ── Perfil nacional (§5.3.2) ─────────────────────────────────────────────────────────────────────────────────
/**
 * Um `hashchange` que deixe o perfil nacional incoerente (`mapa ≠ candidato` ou `cand ≠ número do perfil`) fecha o
 * perfil. Chamado pelo ouvinte de `hashchange` de navigate.ts antes de `afterRouteChange` (escrita de `ui` só aqui, FE §6.6).
 */
export function closeIncoherentProfile(prev: RouteState, next: RouteState): void {
  const p = ui.value.profile;
  if (p?.office !== 'president') return;
  const national = (r: RouteState) =>
    r.office === 'president' && r.place.kind === 'br' && r.mapMode === 'candidate' && r.mapCandidate === p.number;
  if (national(prev) && !national(next)) ui.value = { ...ui.value, profile: null };
}

// ── afterRouteChange ─────────────────────────────────────────────────────────────────────────────────────────
/** Passos 1–4: fecha busca, perfil, seletor e Sobre; recolhe listas (só quando `viewKey` muda). */
function closePanels(): void {
  const u = ui.value;
  if (!u.searchOpen && u.profile === null && !u.pickerOpen && u.aboutOpen === null && u.expanded === null) return;
  ui.value = { ...u, searchOpen: false, profile: null, pickerOpen: false, aboutOpen: null, expanded: null };
}

/**
 * Efeitos de uma mudança de rota, nesta ordem (§5.1): 1–4 fecham painéis (só se `viewKey` mudou); 6 título; 7 rolagem
 * do `flow` ao topo (só se `viewKey` mudou); 8 foco (só com modalidade de teclado); 9 `then` pendente.
 */
export function afterRouteChange(prev: RouteState, next: RouteState, cause: RouteChangeCause = 'navigate'): void {
  const keyChanged = viewKey(prev) !== viewKey(next);
  if (keyChanged) closePanels();
  applyTitle();
  if (keyChanged && cause !== 'statics' && layout.value.mode === 'flow') options.scrollToTop();
  const target = ui.value.keyboard ? focusTargetFor(prev, next, cause) : null;
  if (target) scheduleFocus(target);
  const then = pendingThen;
  pendingThen = null;
  then?.();
}
